import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Drawer } from '@/shedcnUI/drawer'
import api from '@/api/config'
import { FaCheckCircle, FaClock, FaEnvelope, FaExclamationTriangle, FaSpinner, FaStop, FaTimesCircle } from 'react-icons/fa'
import toast from 'react-hot-toast'
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/shedcnUI/dialog";

interface TaskStatus {
  task_id: string;
  status: string;
  state: string;
  percentage: number;
  total_emails: number;
  sent_emails: number;
  failed_emails: number;
  error_message?: string;
  message?: string;
  started_at?: string;
  completed_at?: string;
  delay_minutes?: number;
  strategy?: string;
  estimated_remaining?: string;
  campaign?: {
    uuid: string;
    name: string;
  };
}

interface EmailSendingStatusDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taskId?: string;
  campaignName?: string;
}

const terminalStates = new Set(['completed', 'failed', 'cancelled', 'error'])

function parseBackendDate(iso?: string) {
  if (!iso) return undefined
  const hasTz = /[zZ]|[+-]\d\d:?\d\d$/.test(iso)
  return new Date(hasTz ? iso : `${iso}Z`)
}

const formatTime = (seconds: number) => {
  const minutes = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${minutes}m ${secs}s`
}

function titleCase(raw: string) {
  return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : raw
}

export const EmailSendingStatusDrawer: React.FC<EmailSendingStatusDrawerProps> = ({
  open,
  onOpenChange,
  taskId,
  campaignName,
}) => {
  const [taskStatus, setTaskStatus] = useState<TaskStatus | null>(null)
  const [isCancelling, setIsCancelling] = useState(false)
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)

  // Race protection (mirrors EmailSendingStatusPage)
  const requestSeqRef = useRef(0)
  const cancellingRef = useRef(false)

  const startedAtDate = useMemo(() => parseBackendDate(taskStatus?.started_at), [taskStatus?.started_at])

  const elapsedSeconds = useMemo(() => {
    if (!open || !startedAtDate) return 0
    return Math.max(0, Math.floor((Date.now() - startedAtDate.getTime()) / 1000))
  }, [open, startedAtDate])

  useEffect(() => {
    if (!open) {
      setTaskStatus(null)
      cancellingRef.current = false
      requestSeqRef.current++
    }
  }, [open])

  useEffect(() => {
    if (!open || !taskId) return

    let mounted = true

    const fetchStatus = async () => {
      const seq = ++requestSeqRef.current
      try {
        const response = await api.get(`/api/task/${taskId}/status`)
        if (!mounted || seq !== requestSeqRef.current) return true

        const data = response.data

        let percentage =
          typeof data.percentage === 'number' ? data.percentage : parseInt(data.percentage) || 0
        percentage = Math.max(0, Math.min(100, percentage))

        const next: TaskStatus = {
          task_id: data.task_id || taskId,
          status: data.state || data.status || 'pending',
          state: data.state || data.status || 'pending',
          percentage,
          total_emails: data.total_emails || data.total || 0,
          sent_emails: data.sent_emails || data.sent || 0,
          failed_emails: data.failed_emails || data.failed || 0,
          error_message: data.error_message || data.error || data.message,
          message: data.message,
          started_at: data.started_at,
          completed_at: data.completed_at,
          delay_minutes: data.delay_minutes,
          strategy: data.strategy,
          estimated_remaining: data.estimated_remaining,
          campaign: data.campaign,
        }

        if (cancellingRef.current && next.state === 'running') {
          setTaskStatus(prev => (prev ? { ...prev, ...next, state: prev.state, status: prev.status } : next))
        } else {
          setTaskStatus(next)
        }

        if (terminalStates.has(next.state)) return false
        return true
      } catch (err) {
        console.error('Error fetching sending task status:', err)
        return true
      }
    }

    fetchStatus()

    const intervalId = window.setInterval(async () => {
      const shouldContinue = await fetchStatus()
      if (!shouldContinue) window.clearInterval(intervalId)
    }, 2500)

    return () => {
      mounted = false
      window.clearInterval(intervalId)
    }
  }, [open, taskId])

  const handleCancel = async () => {
    if (!taskId) return
    setShowCancelConfirm(true)
  }

  const confirmCancel = async () => {
    if (!taskId) return

    setShowCancelConfirm(false)
    cancellingRef.current = true
    setIsCancelling(true)

    // Optimistic update
    setTaskStatus(prev => (prev ? { ...prev, state: 'cancelled', status: 'cancelled' } : prev))

    try {
      const res = await api.post(`/api/task/${taskId}/cancel`)
      if (res.data?.success) toast.success('Sending cancelled')
      else toast.error(res.data?.error || 'Failed to cancel')
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to cancel')
    } finally {
      setIsCancelling(false)
    }
  }

  const state = (taskStatus?.state || 'pending').toLowerCase()
  const progress = taskStatus?.percentage || 0

  const isCompleted = state === 'completed'
  const isFailed = ['error', 'failed'].includes(state)
  const isCancelled = state === 'cancelled'
  const isRunning = ['processing', 'running', 'starting', 'pending'].includes(state)

  const badge = (() => {
    if (state === 'running') return { text: 'Sending', cls: 'bg-blue-50 text-blue-700' }
    if (state === 'completed') return { text: 'Completed', cls: 'bg-green-light text-green-dark' }
    if (state === 'cancelled') return { text: 'Cancelled', cls: 'bg-yellow-50 text-yellow-800' }
    if (state === 'failed' || state === 'error') return { text: 'Failed', cls: 'bg-red-light text-red-dark' }
    return { text: titleCase(state || 'Pending'), cls: 'bg-gray-100 text-gray-700' }
  })()

  // SVG Semi-circular progress calculation (mirrors CampaignStatusDrawer)
  const radius = 100
  const strokeWidth = 30
  const circumference = Math.PI * radius
  const offset = circumference - (progress / 100) * circumference

  const strokeColor = isFailed ? '#EF4444' : isCompleted ? '#10B981' : '#006EFF'

  const title = campaignName || taskStatus?.campaign?.name || 'Email Sending Status'

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      width="480px"
      className="rounded-none max-h-full p-2 bg-white shadow-2xl"
    >
      <div className="flex flex-col items-center px-6 pt-4 pb-12 h-full bg-white rounded-xl">
        {/* Title */}
        <h2 className="text-[22px] font-bold text-[#111111] text-center mb-10 w-full max-w-[360px] leading-snug">
          {title}
        </h2>

        {!taskId ? (
          <div className="flex flex-col items-center justify-center py-10 text-gray-500">
            <FaExclamationTriangle className="text-xl mb-2" />
            <div className="text-sm">No sending task found for this campaign.</div>
          </div>
        ) : !taskStatus ? (
          <div className="flex flex-col items-center justify-center py-10 text-gray-500">
            <FaSpinner className="animate-spin text-2xl mb-2" />
            <div className="text-sm">Loading status...</div>
          </div>
        ) : (
          <>
            {/* Progress Arc */}
            <div className="relative w-[280px] h-[140px] flex items-end justify-center overflow-hidden mb-6">
              <svg
                className="absolute top-0 left-0 w-full h-[280px]"
                viewBox="0 0 260 260"
              >
                {/* Background Arc */}
                <path
                  d="M 30,130 A 100,100 0 0,1 230,130"
                  fill="none"
                  stroke="#F3F4F6"
                  strokeWidth={strokeWidth}
                  strokeLinecap="butt"
                />
                {/* Progress Arc */}
                <path
                  d="M 30,130 A 100,100 0 0,1 230,130"
                  fill="none"
                  stroke={strokeColor}
                  strokeWidth={strokeWidth}
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={offset}
                  style={{ transition: 'stroke-dashoffset 0.6s ease-in-out' }}
                />
              </svg>

              {/* Percentage */}
              <div className="absolute bottom-0 flex flex-col items-center">
                <div className="text-[42px] font-extrabold text-[#111111] leading-none">
                  {progress}%
                </div>
                <div className={`mt-2 px-3 py-1 rounded-full text-xs font-semibold ${badge.cls}`}>
                  {badge.text}
                </div>
              </div>
            </div>

            {/* Counters */}
            <div className="grid grid-cols-3 gap-3 w-full max-w-[420px] mb-6">
              <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
                <div className="text-[22px] font-bold text-gray-900">{taskStatus.sent_emails}</div>
                <div className="text-xs text-gray-500">Sent</div>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
                <div className="text-[22px] font-bold text-gray-900">{taskStatus.failed_emails}</div>
                <div className="text-xs text-gray-500">Failed</div>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
                <div className="text-[22px] font-bold text-gray-900">{taskStatus.total_emails}</div>
                <div className="text-xs text-gray-500">Total</div>
              </div>
            </div>

            {/* Details */}
            <div className="w-full max-w-[420px] bg-white border border-[#E3E2E7] rounded-xl p-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Strategy</div>
                  <div className="font-medium text-gray-900">{(taskStatus.strategy || 'round_robin').replace(/_/g, ' ')}</div>
                </div>
                <div>
                  <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Delay</div>
                  <div className="font-medium text-gray-900">{taskStatus.delay_minutes || 0} min</div>
                </div>
                <div>
                  <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Elapsed</div>
                  <div className="font-medium text-gray-900">{formatTime(elapsedSeconds)}</div>
                </div>
                <div>
                  <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Remaining</div>
                  <div className="font-medium text-gray-900">{taskStatus.estimated_remaining || (isRunning ? 'Calculating...' : '-')}</div>
                </div>
              </div>

              {(taskStatus.status === 'failed' || taskStatus.state === 'failed' || isFailed) && (
                <div className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  <FaTimesCircle className="mt-0.5" />
                  <div>
                    <div className="font-semibold">Error</div>
                    <div className="mt-0.5">{taskStatus.error_message || 'Sending failed.'}</div>
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="mt-6 w-full max-w-[420px]">
              <button
                type="button"
                onClick={handleCancel}
                disabled={isCancelling || isCompleted || isCancelled || !taskId}
                className={
                  'w-full h-[44px] rounded-xl border text-sm font-semibold flex items-center justify-center gap-2 ' +
                  (isCancelling || isCompleted || isCancelled
                    ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                    : 'bg-white text-gray-900 border-gray-200 hover:bg-gray-50')
                }
              >
                {isCancelling ? <FaSpinner className="animate-spin" /> : <FaStop />}
                {isCompleted ? 'Completed' : isCancelled ? 'Cancelled' : 'Cancel sending'}
              </button>
            </div>
          </>
        )}
      </div>

      <Dialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
        <DialogContent
          className="max-w-md p-8 overflow-hidden border-none shadow-2xl"
          onClose={() => setShowCancelConfirm(false)}
        >
          <div className="p-0">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-amber-50 rounded-2xl flex items-center justify-center shrink-0">
                <FaExclamationTriangle className="text-amber-500 text-base" />
              </div>
              <div>
                <DialogTitle className="text-xl font-semibold text-[#17150b] leading-tight">
                  Cancel Sending?
                </DialogTitle>
              </div>
            </div>

            <div className="space-y-4">
              <p className="text-[#7d7b78] leading-relaxed">
                Are you sure you want to stop sending emails for this campaign? 
                This action cannot be undone and any remaining unsent emails will not be processed.
              </p>
            </div>

            <div className="flex gap-3 mt-8">
              <button
                className="flex-1 px-4 py-3 bg-[#f8f8f8] hover:bg-gray-200 text-[#17150b] text-sm font-semibold rounded-[10px] transition-all"
                onClick={() => setShowCancelConfirm(false)}
              >
                No, Keep Sending
              </button>
              <button
                className="flex-1 px-4 py-3 bg-[#fef3f2] border border-[#ef4444] whitespace-nowrap text-[#ef4444] text-sm font-semibold rounded-[10px] transition-all flex items-center justify-center gap-2"
                onClick={confirmCancel}
              >
                Yes, Stop Sending
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Drawer>
  )
}
